import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ScrollProvider } from './contexts/ScrollContext';
import { AnnouncementProvider } from './contexts/AnnouncementContext';

// Public Pages
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ServiceRequest from './pages/ServiceRequest';
import Support from './pages/Support';
import Blog from './pages/Blog';
import BlogPostDetail from './pages/BlogPostDetail';
import Careers from './pages/Careers';
import ApplicationForm from './pages/ApplicationForm';

// Dashboard Pages
import DashboardLayout from './Layout/DashboardLayout';
import Dashboard from './pages/Dashboard';
import ServiceRequestsList from './pages/ServiceRequestsList';
import ServiceRequestDetail from './pages/ServiceRequestDetail';
import DashboardServiceRequest from './pages/DashboardServiceRequest';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Projects from './pages/Projects';
import Tasks from './pages/Tasks';

// Training Pages
import UserTrainings from './pages/UserTrainings';
import TrainingDetail from './pages/TrainingDetail';

// Admin Training Management Pages
import TrainingModulesList from './pages/admin/TrainingModulesList';
import TrainingModuleForm from './pages/admin/TrainingModuleForm';
import TrainingContentsList from './pages/admin/TrainingContentsList';
import TrainingContentForm from './pages/admin/TrainingContentForm';
import UserEnrollmentsList from './pages/admin/UserEnrollmentsList';

// Student Progress Tracker (NEW)
import StudentProgressTracker from './pages/admin/StudentProgressTracker';

// Admin Contact Messages Pages
import ContactMessagesList from './pages/admin/ContactMessagesList';
import ContactMessageDetail from './pages/admin/ContactMessageDetail';

// Admin Jobs Management Pages
import AdminJobsList from './pages/admin/AdminJobsList';
import AdminJobForm from './pages/admin/AdminJobForm';
import AdminApplicationsList from './pages/admin/AdminApplicationsList';
import AdminApplicationDetail from './pages/admin/AdminApplicationDetail';

// Admin Blog Management Pages
import AdminCategoriesList from './pages/admin/AdminCategoriesList';
import AdminBlogPostsList from './pages/admin/AdminBlogPostsList';
import AdminBlogPostForm from './pages/admin/AdminBlogPostForm';
import AdminBlogCommentsList from './pages/admin/AdminBlogCommentsList';

// Admin Support Management Pages
import AdminSupportTicketsList from './pages/admin/AdminSupportTicketsList';
import AdminSupportTicketDetail from './pages/admin/AdminSupportTicketDetail';

// Admin Pages (Superadmin only)
import UsersList from './pages/admin/UsersList';
import UserDetail from './pages/admin/UserDetail';
import UserForm from './pages/admin/UserForm';
import AllRequestsList from './pages/admin/AllRequestsList';
import AllRequestDetail from './pages/admin/AllRequestDetail';
import ServicesList from './pages/admin/ServicesList';
import ServiceForm from './pages/admin/ServiceForm';

// Newsletter Admin Pages
import NewsletterDashboard from './pages/admin/NewsletterDashboard';
import SubscribersManagement from './pages/admin/SubscribersManagement';
import CampaignsManagement from './pages/admin/CampaignsManagement';
import CreateCampaign from './pages/admin/CreateCampaign';
import NewsletterSettings from './pages/admin/NewsletterSettings';

// Newsletter Public Pages (ADD THIS)
import ConfirmSubscription from './pages/ConfirmSubscription';

// Portfolio Admin Pages
import AdminProjectsList from './pages/admin/portfolio/AdminProjectsList';
import AdminProjectForm from './pages/admin/portfolio/AdminProjectForm';
import AdminSolutionsList from './pages/admin/portfolio/AdminSolutionsList';
import AdminSolutionForm from './pages/admin/portfolio/AdminSolutionForm';
import PortfolioSettings from './pages/admin/portfolio/PortfolioSettings';

// ==================== CLASS MARKS PAGES ====================
import ClassMarksList from './pages/ClassMarksList';
import CreateClassMarks from './pages/CreateClassMarks';
import ClassMarksDetail from './pages/ClassMarksDetail';
import ClassMarksPrint from './pages/ClassMarksPrint';

// ==================== ANNOUNCEMENT MANAGEMENT ====================
import AnnouncementManagement from './pages/AnnouncementManagement';

// Jackline's Crochet Order Form
import CrochetOrderForm from './components/Jackline/CrochetOrderForm';

// Common Components
import LoadingPage from './components/Common/LoadingPage';
import WhatsAppButton from './components/Common/WhatsAppButton';

// User Applications
import MyApplications from './pages/MyApplications';


const queryClient = new QueryClient();

// Private Route Guard Component
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingPage />;
  
  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Super Admin Only Route Guard
const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingPage />;
  
  if (!user) return <Navigate to="/login" replace />;
  
  const userRole = (user as any).role;
  if (userRole !== 'superadmin') {
    return <Navigate to="/dashboard/main" replace />;
  }
  
  return <>{children}</>;
};

// Admin Or SuperAdmin Route Guard (for pages accessible by both)
const AdminOrSuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingPage />;
  
  if (!user) return <Navigate to="/login" replace />;
  
  const userRole = (user as any).role;
  if (userRole !== 'superadmin' && userRole !== 'admin') {
    return <Navigate to="/dashboard/main" replace />;
  }
  
  return <>{children}</>;
};

// Route Configuration Component
function AppRoutes() {
  return (
    <>
      <Routes>
        {/* ==================== PUBLIC ROUTES ==================== */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:uidb64/:token" element={<ResetPassword />} />
        <Route path="/request-service" element={<ServiceRequest />} />
        <Route path="/support" element={<Support />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPostDetail />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/apply" element={<ApplicationForm />} />
        
        {/* ==================== NEWSLETTER CONFIRMATION (PUBLIC) ==================== */}
        <Route path="/newsletter/confirm/:token" element={<ConfirmSubscription />} />
        
        {/* ==================== CROCHET ORDER FORM (PUBLIC) ==================== */}
        <Route path="/crochet-order" element={<CrochetOrderForm />} />
        
        {/* ==================== PROTECTED DASHBOARD ROUTES ==================== */}
        <Route 
          path="/dashboard" 
          element={
            <PrivateRoute>
              <DashboardLayout />
            </PrivateRoute>
          }
        >
          {/* Default redirect to main dashboard */}
          <Route index element={<Navigate to="/dashboard/main" replace />} />
          
          {/* Dashboard Pages - Works for all authenticated users */}
          <Route path="main" element={<Dashboard />} />
          <Route path="service-requests" element={<ServiceRequestsList />} />
          <Route path="service-request/:id" element={<ServiceRequestDetail />} />
          <Route path="request-service" element={<DashboardServiceRequest />} />
          <Route path="profile" element={<Profile />} />
          <Route path="settings" element={<Settings />} />
          <Route path="projects" element={<Projects />} />
          <Route path="tasks" element={<Tasks />} />
          
          {/* ==================== MY APPLICATIONS ROUTE (FIXED - Relative path) ==================== */}
          <Route path="my-applications" element={<MyApplications />} />
          
          {/* ==================== CLASS MARKS ROUTES ==================== */}
          <Route path="class-marks" element={<ClassMarksList />} />
          <Route path="class-marks/create" element={<CreateClassMarks />} />
          <Route path="class-marks/:id" element={<ClassMarksDetail />} />
          <Route path="class-marks/:id/print" element={<ClassMarksPrint />} />
          
          {/* Training Routes - For all users */}
          <Route path="trainings" element={<UserTrainings />} />
          <Route path="training/:enrollmentId" element={<TrainingDetail />} />
          
          {/* ==================== ADMIN & SUPER ADMIN ROUTES ==================== */}
          
          {/* Training Management - Accessible by Admin and SuperAdmin */}
          <Route path="admin/training/modules" element={
            <AdminOrSuperAdminRoute>
              <TrainingModulesList />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/training/modules/create" element={
            <AdminOrSuperAdminRoute>
              <TrainingModuleForm />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/training/modules/:id" element={
            <AdminOrSuperAdminRoute>
              <TrainingContentsList />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/training/modules/:id/edit" element={
            <AdminOrSuperAdminRoute>
              <TrainingModuleForm />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/training/contents/:contentId/edit" element={
            <AdminOrSuperAdminRoute>
              <TrainingContentForm />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/training/modules/:moduleId/contents/create" element={
            <AdminOrSuperAdminRoute>
              <TrainingContentForm />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/training/enrollments" element={
            <AdminOrSuperAdminRoute>
              <UserEnrollmentsList />
            </AdminOrSuperAdminRoute>
          } />
          
          {/* Student Progress Tracker - Accessible by Admin and SuperAdmin */}
          <Route path="admin/student-progress" element={
            <AdminOrSuperAdminRoute>
              <StudentProgressTracker />
            </AdminOrSuperAdminRoute>
          } />
          
          {/* Portfolio Management - Accessible by Admin and SuperAdmin */}
          <Route path="admin/portfolio/projects" element={
            <AdminOrSuperAdminRoute>
              <AdminProjectsList />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/portfolio/projects/create" element={
            <AdminOrSuperAdminRoute>
              <AdminProjectForm />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/portfolio/projects/:id/edit" element={
            <AdminOrSuperAdminRoute>
              <AdminProjectForm />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/portfolio/solutions" element={
            <AdminOrSuperAdminRoute>
              <AdminSolutionsList />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/portfolio/solutions/create" element={
            <AdminOrSuperAdminRoute>
              <AdminSolutionForm />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/portfolio/solutions/:id/edit" element={
            <AdminOrSuperAdminRoute>
              <AdminSolutionForm />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/portfolio/settings" element={
            <AdminOrSuperAdminRoute>
              <PortfolioSettings />
            </AdminOrSuperAdminRoute>
          } />
          
          {/* ==================== ANNOUNCEMENT MANAGEMENT ==================== */}
          <Route path="admin/announcements" element={
            <AdminOrSuperAdminRoute>
              <AnnouncementManagement />
            </AdminOrSuperAdminRoute>
          } />
          
          {/* ==================== SUPER ADMIN ONLY ROUTES ==================== */}
          
          {/* Super Admin Contact Messages */}
          <Route path="admin/contact" element={
            <SuperAdminRoute>
              <ContactMessagesList />
            </SuperAdminRoute>
          } />
          <Route path="admin/contact/:id" element={
            <SuperAdminRoute>
              <ContactMessageDetail />
            </SuperAdminRoute>
          } />
          
          {/* Super Admin Jobs Management */}
          <Route path="admin/jobs" element={
            <SuperAdminRoute>
              <AdminJobsList />
            </SuperAdminRoute>
          } />
          <Route path="admin/jobs/create" element={
            <SuperAdminRoute>
              <AdminJobForm />
            </SuperAdminRoute>
          } />
          <Route path="admin/jobs/:id/edit" element={
            <SuperAdminRoute>
              <AdminJobForm />
            </SuperAdminRoute>
          } />
          <Route path="admin/applications" element={
            <SuperAdminRoute>
              <AdminApplicationsList />
            </SuperAdminRoute>
          } />
          <Route path="admin/applications/:id" element={
            <SuperAdminRoute>
              <AdminApplicationDetail />
            </SuperAdminRoute>
          } />
          
          {/* Super Admin Blog Management */}
          <Route path="admin/blog/categories" element={
            <SuperAdminRoute>
              <AdminCategoriesList />
            </SuperAdminRoute>
          } />
          <Route path="admin/blog/posts" element={
            <SuperAdminRoute>
              <AdminBlogPostsList />
            </SuperAdminRoute>
          } />
          <Route path="admin/blog/posts/create" element={
            <SuperAdminRoute>
              <AdminBlogPostForm />
            </SuperAdminRoute>
          } />
          <Route path="admin/blog/posts/:id/edit" element={
            <SuperAdminRoute>
              <AdminBlogPostForm />
            </SuperAdminRoute>
          } />
          <Route path="admin/blog/comments" element={
            <SuperAdminRoute>
              <AdminBlogCommentsList />
            </SuperAdminRoute>
          } />
          
          {/* Super Admin Support Management */}
          <Route path="admin/support/tickets" element={
            <SuperAdminRoute>
              <AdminSupportTicketsList />
            </SuperAdminRoute>
          } />
          <Route path="admin/support/tickets/:id" element={
            <SuperAdminRoute>
              <AdminSupportTicketDetail />
            </SuperAdminRoute>
          } />
          
          {/* Newsletter Management - Accessible by Admin and SuperAdmin */}
          <Route path="admin/newsletter" element={
            <AdminOrSuperAdminRoute>
              <NewsletterDashboard />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/newsletter/subscribers" element={
            <AdminOrSuperAdminRoute>
              <SubscribersManagement />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/newsletter/campaigns" element={
            <AdminOrSuperAdminRoute>
              <CampaignsManagement />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/newsletter/create" element={
            <AdminOrSuperAdminRoute>
              <CreateCampaign />
            </AdminOrSuperAdminRoute>
          } />
          <Route path="admin/newsletter/settings" element={
            <AdminOrSuperAdminRoute>
              <NewsletterSettings />
            </AdminOrSuperAdminRoute>
          } />
          
          {/* Super Admin User Management */}
          <Route path="admin/users" element={
            <SuperAdminRoute>
              <UsersList />
            </SuperAdminRoute>
          } />
          <Route path="admin/users/create" element={
            <SuperAdminRoute>
              <UserForm />
            </SuperAdminRoute>
          } />
          <Route path="admin/users/:id" element={
            <SuperAdminRoute>
              <UserDetail />
            </SuperAdminRoute>
          } />
          <Route path="admin/users/:id/edit" element={
            <SuperAdminRoute>
              <UserForm />
            </SuperAdminRoute>
          } />
          
          {/* Super Admin Service Requests */}
          <Route path="admin/requests" element={
            <SuperAdminRoute>
              <AllRequestsList />
            </SuperAdminRoute>
          } />
          <Route path="admin/requests/:id" element={
            <SuperAdminRoute>
              <AllRequestDetail />
            </SuperAdminRoute>
          } />
          
          {/* Super Admin Services Management */}
          <Route path="admin/services" element={
            <SuperAdminRoute>
              <ServicesList />
            </SuperAdminRoute>
          } />
          <Route path="admin/services/create" element={
            <SuperAdminRoute>
              <ServiceForm />
            </SuperAdminRoute>
          } />
          <Route path="admin/services/:id/edit" element={
            <SuperAdminRoute>
              <ServiceForm />
            </SuperAdminRoute>
          } />
        </Route>
        
        {/* ==================== CATCH ALL ROUTE ==================== */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* ==================== WHATSAPP BUTTON (GLOBAL) ==================== */}
      <WhatsAppButton />
    </>
  );
}

// Main App Component
function App() {
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (initialLoading) {
    return <LoadingPage />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <ScrollProvider>
            <AnnouncementProvider>
              <AppRoutes />
            </AnnouncementProvider>
          </ScrollProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;